/*
  Pressable micro-interaction directive `v-press` (HAP-1891, parent HAP-1884
  Phase 3.1). One consistent, tactile press for buttons, cards and list rows:
  a spring scale-down while pressed that springs back on release, plus an
  optional haptic pulse on capable devices. Replaces ad-hoc `active:scale-*`
  with the shared P0.2 motion language (motion.js tokens + useHaptics).

  Usage:
    <button v-press>…</button>      // default 0.96 scale + light haptic
    <div v-press="0.92">…</div>     // custom press scale (smaller = deeper)
    <li v-press:medium>…</li>       // pick a haptic pattern (light is default)
    <a v-press:none>…</a>           // scale only, no haptic

  Why a directive driven by the Web Animations API (not a CSS `:active` rule):
    • Same spring + haptic language the rest of the motion system uses, and it
      works for touch AND mouse via pointer events.
    • WAAPI animates `transform` on its own timeline, so we NEVER write the
      element's inline `transform`/`transition` — existing CSS hover transitions
      or transforms on the same element are left untouched (no clobbering).
    • Reduced motion drops the SCALE entirely (no transform jump at all), which
      a plain `:active { transform }` rule can't do cleanly (the global CSS
      guard only collapses durations, it wouldn't remove the scale).

  No layout change: `transform: scale()` is compositor-only and never reflows.
*/
import { useHaptics } from '../composables/useHaptics'
import { DURATION, EASING } from '../helpers/motion'

const haptics = useHaptics()

const DEFAULT_SCALE = 0.96

// Live reduced-motion check. JS-driven motion must neutralize itself the same
// way the CSS guard does (memory: HAP-1886). Under reduced motion we skip the
// scale animation entirely — haptics are non-visual and still fire.
const reducedMotionMQ =
	typeof window !== 'undefined' && typeof window.matchMedia === 'function'
		? window.matchMedia('(prefers-reduced-motion: reduce)')
		: { matches: false }

// Animate from the last logical scale to a new one on the element's own WAAPI
// timeline. We track `__pressScale` so an interrupted press (rapid taps) starts
// from where it logically was rather than snapping to 1 first. `fill: forwards`
// holds the end state; the next call cancels + replaces before the next paint,
// so there's no visible flash from the cancel.
function animateTo(el, toScale) {
	if (reducedMotionMQ.matches) return
	const from = el.__pressScale ?? 1
	el.__pressScale = toScale
	if (el.__pressAnim) el.__pressAnim.cancel()
	const anim = el.animate(
		[{ transform: `scale(${from})` }, { transform: `scale(${toScale})` }],
		{ duration: DURATION.fast, easing: EASING.spring, fill: 'forwards' },
	)
	el.__pressAnim = anim
	// Once the element has sprung back to rest, drop the WAAPI hold so it's back
	// under pure CSS control — otherwise `fill: forwards` would pin scale(1) and
	// suppress any CSS `:hover` transform the element also defines.
	if (toScale === 1) {
		anim.onfinish = () => {
			if (el.__pressAnim !== anim) return
			anim.cancel()
			el.__pressAnim = null
			el.__pressScale = 1
		}
	}
}

export const pressDirective = {
	mounted(el, binding) {
		const scale = typeof binding.value === 'number' ? binding.value : DEFAULT_SCALE
		const hapticKind = binding.arg === 'none' ? null : binding.arg || 'light'

		const down = () => {
			// Fire the buzz inside the user gesture (Vibration API requirement).
			if (hapticKind) (haptics[hapticKind] || haptics.light)()
			animateTo(el, scale)
		}
		// pointerup / cancel / leave all release: leaving the element while held
		// (a drag-off) should spring back just like lifting the finger.
		const up = () => animateTo(el, 1)

		el.__press = { down, up }
		el.addEventListener('pointerdown', down, { passive: true })
		el.addEventListener('pointerup', up, { passive: true })
		el.addEventListener('pointercancel', up, { passive: true })
		el.addEventListener('pointerleave', up, { passive: true })
	},
	unmounted(el) {
		if (!el.__press) return
		if (el.__pressAnim) el.__pressAnim.cancel()
		el.removeEventListener('pointerdown', el.__press.down)
		el.removeEventListener('pointerup', el.__press.up)
		el.removeEventListener('pointercancel', el.__press.up)
		el.removeEventListener('pointerleave', el.__press.up)
		delete el.__press
		delete el.__pressAnim
		delete el.__pressScale
	},
}

export default pressDirective
